import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { replaceMarkdown } from "./mathjax";
import "./mathjax.scss";
import { showSuccessToast } from "../Toaster";

interface MathJaxForChatPageProps {
  content: string;
  isSolution?: boolean;
  onRenderComplete?: () => void;
  instant?: boolean;
  mathJaxSpeedMs?: number;
  textSpeedMs?: number;
  onAfterRender?: () => void;
  onTypingProgress?: () => void;
  onNavigate?: (route: string) => void;
}

// --- Helper Functions ---

/**
 * Attaches LaTeX source data to rendered MathJax nodes.
 * @param container The container element containing MathJax rendered content.
 */
const applyLatexDataToContainer = (container: HTMLElement) => {
  if (!container || !window.MathJax) return;
  try {
    const doc = window.MathJax.startup.document;
    if (typeof doc.getMathItemsWithin === 'function') {
      const mathItems = doc.getMathItemsWithin(container);
      if (mathItems?.length > 0) {
        mathItems.forEach((item: any) => {
          const el = item.typesetRoot;
          if (el) {
            el.setAttribute('data-tex', item.math);
            el.setAttribute('data-display', item.display ? 'true' : 'false');
          }
        });
      }
    }
  } catch (e) {
    console.error("Error applying LaTeX data:", e);
  }
};

/**
 * Formats LaTeX content logic (inline vs display).
 */
const formatTex = (tex: string, isDisplay: boolean) => {
  const cleanTex = tex.trim();
  return isDisplay ? `$$${cleanTex}$$` : `$${cleanTex}$`;
};

/**
 * Fallback copy mechanism using textarea.
 */
const fallbackCopyTextToClipboard = (text: string, onSuccess: () => void) => {
  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.opacity = "0";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);

    if (successful) onSuccess();
    else console.error('Fallback: Unable to copy');
  } catch (err) {
    console.error('Fallback: Unable to copy', err);
  }
};

/**
 * Main copy function that attempts Clipboard API first, then fallback.
 */
const copyTexToClipboard = async (text: string, onSuccess: () => void) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      onSuccess();
    } catch (err) {
      console.error("Clipboard API failed, trying fallback:", err);
      fallbackCopyTextToClipboard(text, onSuccess);
    }
  } else {
    fallbackCopyTextToClipboard(text, onSuccess);
  }
};

// --- Component ---

const MathJaxForChatPage: React.FC<MathJaxForChatPageProps> = ({
  content,
  isSolution = false,
  onRenderComplete,
  instant = false,
  mathJaxSpeedMs = 0,
  textSpeedMs = 0,
  onAfterRender,
  onTypingProgress,
  onNavigate,
}) => {
  // AI Agent V2 SPA navigation helper
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [displayedContent, setDisplayedContent] = useState<string>("");
  const divRef = useRef<HTMLDivElement>(null);
  const hiddenDivRef = useRef<HTMLDivElement>(null);
  const typingTimeout = useRef<number | null>(null);
  const isMounted = useRef(true);
  const hasRendered = useRef<string | null>(null);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Refs to hold the latest callback functions
  const onRenderCompleteRef = useRef(onRenderComplete);
  const onAfterRenderRef = useRef(onAfterRender);
  const onTypingProgressRef = useRef(onTypingProgress);

  // Update refs whenever props change
  useEffect(() => {
    onRenderCompleteRef.current = onRenderComplete;
    onAfterRenderRef.current = onAfterRender;
    onTypingProgressRef.current = onTypingProgress;
  }, [onRenderComplete, onAfterRender, onTypingProgress]);

  // Memoized Success Toast wrappers
  const onFormulaCopySuccess = useCallback(() => showSuccessToast(t('formulaCopiedSuccess')), [t]);
  const onTextCopySuccess = useCallback(() => showSuccessToast(t('textCopiedSuccess')), [t]);

  // Handle Copy Event (Ctrl+C)
  const handleCopy = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    // 1. Check for single contained MathJax element
    const range = selection.getRangeAt(0);
    const commonNode = range.commonAncestorContainer;
    const commonElement = commonNode.nodeType === Node.ELEMENT_NODE 
      ? (commonNode as HTMLElement) 
      : commonNode.parentElement;
    const parentMath = commonElement?.closest('[data-tex]');

    if (parentMath) {
      const tex = parentMath.getAttribute('data-tex');
      const isDisplay = parentMath.getAttribute('data-display') === 'true';
      if (tex) {
        e.preventDefault();
        const formattedTex = formatTex(tex, isDisplay);
        if (e.clipboardData) {
          e.clipboardData.setData('text/plain', formattedTex);
          e.clipboardData.setData('text/html', formattedTex);
          onTextCopySuccess();
        }
      }
      return;
    }

    // 2. Handle mixed selection (Text + MathJax)
    // Create a clone to manipulate and extract MathJax data
    const clone = range.cloneContents();
    const div = document.createElement('div');
    div.appendChild(clone);

    const mathElements = div.querySelectorAll('[data-tex]');
    
    // Replace all MathJax placeholders in the clone with their LaTeX source
    if (mathElements.length > 0) {
      e.preventDefault();
      mathElements.forEach((el) => {
        const tex = el.getAttribute('data-tex');
        const isDisplay = el.getAttribute('data-display') === 'true';
        if (tex) {
          const formattedTex = formatTex(tex, isDisplay);
          el.replaceWith(document.createTextNode(formattedTex));
        }
      });

      if (e.clipboardData) {
        e.clipboardData.setData('text/plain', div.textContent || '');
        e.clipboardData.setData('text/html', div.innerHTML);
        onTextCopySuccess();
      }
    } else {
      // 3. Fallback for pure text - verify content exists
      if (div.textContent && div.textContent.trim().length > 0) {
        // We allow default action but trigger toast
        setTimeout(onTextCopySuccess, 0);
      }
    }
  }, [onTextCopySuccess]);

  // Handle Click Event (Click-to-Copy for Formulas and SPA Link Hijacking)
  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    // SPA Link Hijacking: intercept clicks on custom navigation links
    const anchor = target.closest("a");
    if (anchor && anchor.getAttribute("href")?.startsWith("/")) {
      e.stopPropagation();
      e.preventDefault();
      const route = anchor.getAttribute("href")!;
      if (onNavigate) {
        onNavigate(route);
      } else {
        navigate(route);
      }
      return;
    }

    const mathNode = target.closest('[data-tex]');

    if (!mathNode) return;

    // Avoid interfering if user is selecting text *inside* this specific formula
    const selection = window.getSelection();
    const isSelectingThisNode = selection &&
      !selection.isCollapsed &&
      selection.toString().length > 0 &&
      mathNode.contains(selection.anchorNode);

    if (isSelectingThisNode) return;

    const tex = mathNode.getAttribute('data-tex');
    const isDisplay = mathNode.getAttribute('data-display') === 'true';

    if (tex) {
      const formattedTex = formatTex(tex, isDisplay);
      copyTexToClipboard(formattedTex, onFormulaCopySuccess);
      e.stopPropagation();
      e.preventDefault();
    }
  }, [onFormulaCopySuccess, navigate, onNavigate]);

  // Main Effect: Render and Type Animation
  useEffect(() => {
    // If content is identical, try to maintain MathJax state or just return
    if (hasRendered.current === content) {
      if (window.MathJax && divRef.current && document.contains(divRef.current)) {
        try {
          window.MathJax.typesetClear([divRef.current]);
          window.MathJax.typesetPromise([divRef.current]).catch((err: any) =>
            console.error("MathJax re-typeset error:", err)
          );
        } catch (err) {
          console.error("MathJax sync re-typeset error:", err);
        }
      }
      return;
    }

    // 1. Prepare Content
    const formattedContent = replaceMarkdown(content, isSolution);

    if (instant) {
      setDisplayedContent(formattedContent);
      hasRendered.current = content;
      onRenderCompleteRef.current?.();
      onAfterRenderRef.current?.();
      return;
    }

    const runRenderSequence = async () => {
      // 2. Pre-render invisible div to let MathJax process and we can segment it
      if (!hiddenDivRef.current) return;
      hiddenDivRef.current.innerHTML = formattedContent;

      if (window.MathJax && hiddenDivRef.current && isMounted.current) {
        try {
          window.MathJax.typesetClear([hiddenDivRef.current]);
          await window.MathJax.typesetPromise([hiddenDivRef.current]);
          if (isMounted.current && hiddenDivRef.current) {
            applyLatexDataToContainer(hiddenDivRef.current);
          }
        } catch (err) {
          console.error("MathJax pre-render error:", err);
        }
      }

      if (!isMounted.current || !hiddenDivRef.current) return;

      // 3. Segment the nodes for typing animation
      const nodes = Array.from(hiddenDivRef.current.childNodes);
      const segments: { content: string; isMathJax: boolean }[] = nodes.map(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          const isMathJax = element.classList.contains("MathJax") || !!element.querySelector(".MathJax");
          return { content: element.outerHTML, isMathJax };
        }
        return { content: node.textContent || "", isMathJax: false };
      });

      // 5. Typing Animation Mode
      setDisplayedContent("");
      let currentSegmentIndex = 0;
      let charIndex = 0;

      const finishTyping = () => {
        hasRendered.current = content; // Mark as done
        
        // Final typeset on the visible container to ensure everything looks right
        if (divRef.current && window.MathJax && isMounted.current && document.contains(divRef.current)) {
          try {
            window.MathJax.typesetClear([divRef.current]);
            window.MathJax.typesetPromise([divRef.current])
              .then(() => {
                if (isMounted.current) {
                  onRenderCompleteRef.current?.();
                  onAfterRenderRef.current?.();
                }
              })
              .catch((e: any) => console.error("Final typeset error:", e));
          } catch (err) {
            console.error("MathJax sync final typeset error:", err);
          }
          
          // Observer fallback for loose hydration issues
          const observer = new MutationObserver(() => onAfterRenderRef.current?.());
          observer.observe(divRef.current, { childList: true, subtree: true, characterData: true });
          setTimeout(() => observer.disconnect(), 1000);
        }
      };

      const typeLoop = () => {
        if (currentSegmentIndex >= segments.length) {
          finishTyping();
          return;
        }

        const segment = segments[currentSegmentIndex];
        const stepSize = segment.isMathJax ? 16 : 4; 
        const speed = segment.isMathJax ? mathJaxSpeedMs : textSpeedMs;
        
        charIndex += stepSize;
        const currentSlice = segment.content.slice(0, charIndex);

        // Reconstruct previous segments + current partial segment
        // Optimization: We could cache the 'previousContent' string instead of joining every frame
        const previousContent = segments.slice(0, currentSegmentIndex).map(s => s.content).join("");
        setDisplayedContent(previousContent + currentSlice);

        if (charIndex % (segment.isMathJax ? 64 : 16) === 0) {
          onTypingProgressRef.current?.();
        }

        if (charIndex < segment.content.length) {
          typingTimeout.current = window.setTimeout(typeLoop, speed);
        } else {
          // Segment done, move to next
          currentSegmentIndex++;
          charIndex = 0;
          typingTimeout.current = window.setTimeout(typeLoop, 0); // Immediate next
        }
      };

      typeLoop();
    };

    runRenderSequence();

    return () => {
      if (typingTimeout.current) {
        clearTimeout(typingTimeout.current);
        typingTimeout.current = null;
      }
    };
  }, [content, isSolution, instant, mathJaxSpeedMs, textSpeedMs]);

  // Effect for Instant mode MathJax processing
  useEffect(() => {
    if (instant && displayedContent && window.MathJax && divRef.current) {
      const timeoutId = setTimeout(() => {
        if (isMounted.current && divRef.current && document.contains(divRef.current)) {
          try {
            window.MathJax.typesetClear([divRef.current]);
            window.MathJax.typesetPromise([divRef.current]).catch((err: any) =>
              console.error("MathJax instant typeset error:", err)
            );
          } catch (err) {
            console.error("MathJax sync instant typeset error:", err);
          }
        }
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [displayedContent, instant]);

  return (
    <>
      <div ref={hiddenDivRef} style={{ display: "none" }} />
      <div 
        className="mathjax-chat-content"
        ref={divRef}
        onCopy={handleCopy}
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: displayedContent }}
      />
    </>
  );
};

export default MathJaxForChatPage;